import { TableColumn, TableRow } from '@rio-cloud/rio-uikit/Table';
import { useIntl } from 'react-intl';

import { VehicleWarningIcon } from '#components/resources/VehiclePlate.tsx';
import { OrderState } from '#components/shared/OrderState.tsx';
import { getOrderIcon } from '#components/shared/orderIcons.ts';
import { ServiceTypeName } from '#components/shared/ServiceTypeName.tsx';
import { StopInfo } from '#components/shared/StopInfo.tsx';
import { isServiceOrder } from '#domain/Order.ts';
import { getVehicleWarningState } from '#domain/VehicleWarning.ts';
import type { OrderWithSortableKey } from '#utils/orders.util.ts';

import type { OrderTableColumnKey } from './OrderTable.tsx';

type OrderTableRowProps = {
    order: OrderWithSortableKey;
    visibleColumnKeys?: OrderTableColumnKey[];
};

const defaultVisibleColumnKeys: OrderTableColumnKey[] = [
    'type',
    'number',
    'customerName',
    'loadingAddressEndDate',
    'unloadingAddressEndDate',
    'tourName',
    'truckName',
    'statusWeight',
];

export const OrderTableRow = ({ order, visibleColumnKeys = defaultVisibleColumnKeys }: OrderTableRowProps) => {
    const intl = useIntl();
    const orderStatusIsOpen = order.status === 'open';
    const truckWarningState = getVehicleWarningState({
        hasOrderCommunicationLicense: order.truck?.hasOrderCommunicationLicense,
        hasPocketDriverDataSource: order.truck?.hasPocketDriverDataSource,
    });
    const primaryStop = isServiceOrder(order) ? order.executionStop : order.loadingStop;

    return (
        <TableRow
            key={order.id}
            id={order.id}
            selectable={orderStatusIsOpen}
            data-testid={`order-table-row-${order.id}`}
        >
            {visibleColumnKeys.map(columnKey => {
                switch (columnKey) {
                    case 'type':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                <span
                                    className={`uikit-icon ${getOrderIcon(order)}`}
                                    title={intl.formatMessage({
                                        id: isServiceOrder(order)
                                            ? 'intl-msg:ordercom-cloud-frontend.order.type.service'
                                            : 'intl-msg:ordercom-cloud-frontend.order.type.transport',
                                    })}
                                />
                            </TableColumn>
                        );
                    case 'number':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                <span>
                                    {isServiceOrder(order) ? (
                                        <ServiceTypeName value={order.number} category={order.category} />
                                    ) : (
                                        order.number
                                    )}
                                </span>
                            </TableColumn>
                        );
                    case 'customerName':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                <span>{order.customerName}</span>
                            </TableColumn>
                        );
                    case 'loadingAddressEndDate':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                <StopInfo
                                    name={
                                        isServiceOrder(order)
                                            ? order.company || (
                                                  <ServiceTypeName value={order.number} category={order.category} />
                                              )
                                            : primaryStop.name
                                    }
                                    location={primaryStop.location}
                                    from={primaryStop.startTarget}
                                    to={primaryStop.endTarget}
                                />
                            </TableColumn>
                        );
                    case 'unloadingAddressEndDate':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                {!isServiceOrder(order) && (
                                    <StopInfo
                                        name={order.unloadingStop?.name}
                                        location={order.unloadingStop.location}
                                        from={order.unloadingStop?.startTarget}
                                        to={order.unloadingStop?.endTarget}
                                    />
                                )}
                            </TableColumn>
                        );
                    case 'tourName':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                <span className='white-space-nowrap' title={order.tourName}>
                                    {order.tourName}
                                </span>
                            </TableColumn>
                        );
                    case 'truckName':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                <span className='white-space-nowrap' title={order.truck?.name}>
                                    {order.truck?.name}
                                    <VehicleWarningIcon warningState={truckWarningState} />
                                </span>
                            </TableColumn>
                        );
                    case 'statusWeight':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey} className='white-space-nowrap'>
                                <span className='white-space-nowrap'>
                                    {order.status && <OrderState status={order.status} />}
                                </span>
                            </TableColumn>
                        );
                    default:
                        return null;
                }
            })}
        </TableRow>
    );
};
